import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getTenants, addTenantToUser } from '../../api/backendApi';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * TenantDropdown component for inline tenant assignment in user tables
 *
 * @param {Object} props
 * @param {string} props.userId - User ID for tenant assignment
 * @param {string} props.currentTenantId - Current tenant ID of the user (if any)
 * @param {Function} props.onTenantAssigned - Callback when tenant assignment completes successfully
 */
const TenantDropdown = ({ userId, currentTenantId, onTenantAssigned }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState(currentTenantId || '');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTenantId, setPendingTenantId] = useState(null);

  // Fetch tenants on component mount
  useEffect(() => {
    fetchTenants();
  }, []);

  // Update selected tenant when currentTenantId changes
  useEffect(() => {
    setSelectedTenantId(currentTenantId || '');
  }, [currentTenantId]);

  // Fetch all tenants from the API
  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      const tenantsData = await getTenants();
      setTenants(tenantsData);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      toast.error('Failed to load tenant options');
    } finally {
      setLoadingTenants(false);
    }
  };

  // Handle tenant selection change
  const handleTenantChange = e => {
    const newTenantId = e.target.value;

    // If there's already a tenant assigned, show confirmation
    if (currentTenantId && newTenantId !== currentTenantId && newTenantId !== '') {
      setPendingTenantId(newTenantId);
      setShowConfirmation(true);
      return;
    }

    // Otherwise, proceed with assignment
    if (newTenantId !== selectedTenantId) {
      assignTenant(newTenantId);
    }
  };

  // Handle confirmation dialog responses
  const handleConfirmation = confirmed => {
    setShowConfirmation(false);

    if (confirmed && pendingTenantId) {
      assignTenant(pendingTenantId);
    } else {
      // Reset dropdown to current tenant if user cancels
      setSelectedTenantId(currentTenantId || '');
    }

    setPendingTenantId(null);
  };

  // Assign tenant to user
  const assignTenant = async tenantId => {
    setLoading(true);

    try {
      // If tenantId is empty, we're clearing the tenant
      if (tenantId === '') {
        // TODO: Implement removeTenantFromUser if needed
        toast.error('Removing tenant is not implemented');
        setSelectedTenantId(currentTenantId || '');
        return;
      }

      // Assign the selected tenant to the user
      await addTenantToUser(userId, tenantId);
      setSelectedTenantId(tenantId);
      toast.success('Tenant assigned successfully');

      // Notify parent component that tenant was assigned
      if (onTenantAssigned) {
        onTenantAssigned(userId, tenantId);
      }
    } catch (err) {
      console.error('Error assigning tenant:', err);
      toast.error('Failed to assign tenant');
      // Reset to previous selection on error
      setSelectedTenantId(currentTenantId || '');
    } finally {
      setLoading(false);
    }
  };

  // Render confirmation dialog
  const renderConfirmationDialog = () => {
    if (!showConfirmation) return null;

    const newTenantName = tenants.find(t => t.id === pendingTenantId)?.name || pendingTenantId;
    const currentTenantName = tenants.find(t => t.id === currentTenantId)?.name || currentTenantId;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Change Primary Tenant?</h3>
          <p
            className="mb-4 text-sm text-gray-600"
            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            Set this user's primary tenant from{' '}
            <strong>{currentTenantName || 'No Tenant'}</strong> to{' '}
            <strong>{newTenantName}</strong>? Existing memberships for other tenants will be kept.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              onClick={() => handleConfirmation(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              onClick={() => handleConfirmation(true)}
            >
              Confirm Change
            </button>
          </div>
        </div>
      </div>
    );
  };

  // If loading dropdown options or tenant assignment is in progress
  if (loadingTenants) {
    return <span className="text-gray-400 italic">Loading tenants...</span>;
  }

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
          <LoadingSpinner size={8} />
        </div>
      )}
      <select
        className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        value={selectedTenantId}
        onChange={handleTenantChange}
        disabled={loading}
      >
        <option value="">— No Tenant —</option>
        {tenants.map(tenant => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name || tenant.id}
          </option>
        ))}
      </select>
      {renderConfirmationDialog()}
    </div>
  );
};

export default TenantDropdown;
